// Google Analytics (GA4), kept to the questions in docs/TRACKING.md and nothing
// else. This file is the whole of it: it loads gtag.js, reports page views,
// and turns a click on a [data-track] element into one event.
//
//   page_view               every page a visitor opens (GA adds the country)
//   <data-track value>      a click on an element carrying data-track
//   menu_<route name>       a page of a project being opened
//   google_continue         "Continue with Google" (oauth.ts)
//   signup_newsletter_off   the newsletter box being unticked (signup.vue)
//   article_read            an article shown on its own page (article.vue)
//   upload_batch_done       a Files-page upload batch finishing (file.ts): how many files
//                           were stored, failed, skipped, reindexed and skipped as
//                           duplicates (same name and size already uploaded), counts only
//   upload_batch_indexed    the files of a batch all having an indexing verdict
//                           (upload_summary.ts): indexed, failed, minutes since the first
//                           file was queued, counts only
//   upload_notify           the summary's "Notify me when indexing finishes" button having
//                           been answered (upload_summary.ts): target granted, denied or
//                           dismissed
//
// Where a visitor came from is read off the address they arrived on: the
// campaign labels (utm_*), or the short form `?social=ig`, which is reported as
// the source "instagram" with the medium "social". It works on every page.
//
// Two rules keep the numbers honest:
//   - A data-track value starting with "landing_" is reported only while the
//     landing page is the page on screen. That is how the theme toggle and the
//     news banner, which live outside landing.vue, count for the landing page
//     and for no other.
//   - Nothing that identifies a person or a project is sent: no user id, no
//     project id, no query string. A project page is reported by its route
//     pattern (/my-projects/:service/users).
//
// The same settings apply in every country. Visits are counted everywhere, and
// the advertising side of the tag is switched off everywhere.
import type { Router, RouteLocationNormalized } from "vue-router";

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

const MEASUREMENT_ID = (import.meta.env.VITE_GTAG as string | undefined) || "";

/** Route name of the landing page. */
const LANDING_ROUTE = "home";
const LANDING_PREFIX = "landing_";
/** Route pattern every page of a project starts with. */
const PROJECT_PATTERN = "/my-projects/:service";
/** Route params that are safe to report as they are. */
const PUBLIC_PARAMS = ["slug"];
/** Campaign labels, the ad click ids below and the social label are the only part of a query string that is reported. */
const CAMPAIGN_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
/**
 * The click ids an ad network appends to a link: Google Ads (`gclid`, `gclsrc`, `gbraid`,
 * `wbraid`), Display and Video 360 (`dclid`), Merchant Center (`srsltid`) and Microsoft Ads
 * (`msclkid`). Analytics reads them from the first page's address to file the visit under
 * Paid Search; dropped, a paid click from Google is filed as Organic Search. They identify
 * a click, not a person.
 */
const CLICK_ID_PARAMS = ["gclid", "gclsrc", "gbraid", "wbraid", "dclid", "srsltid", "msclkid"];
/** The short label of a link posted on a social network: `/?social=ig`. */
const SOCIAL_PARAM = "social";
/** What a social label is reported as. A value not listed here is reported as written. */
const SOCIAL_SOURCES: Record<string, string> = {
    ig: "instagram",
    insta: "instagram",
    fb: "facebook",
    th: "threads",
    tw: "x",
    twitter: "x",
    dev: "dev.to",
    devto: "dev.to",
    li: "linkedin",
    yt: "youtube",
    tt: "tiktok",
    gh: "github",
    rd: "reddit",
    hn: "hackernews",
    ph: "producthunt",
    md: "medium",
    dc: "discord",
    bs: "bluesky",
    bsky: "bluesky",
    ms: "mastodon",
    tg: "telegram",
    wa: "whatsapp",
    kt: "kakaotalk",
    kakao: "kakaotalk",
};
const SOCIAL_MEDIUM = "social";
/** Campaign name of a visit that came through a social label. */
const SOCIAL_CAMPAIGN = "social_link";
/** How long a navigation waits for an event to leave before going anyway. */
const SEND_WAIT_MS = 300;

let started = false;
let router: Router | null = null;
let lastPage = "";
let lastMenu = "";

// gtag.js reads the `arguments` object itself off the data layer; an array of
// the same values is ignored. Hence a plain function and no rest parameter.
function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
}

function routePattern(to: RouteLocationNormalized): string {
    const record = to.matched[to.matched.length - 1];
    let path = record?.path || to.path;
    for (const name of PUBLIC_PARAMS) {
        const value = to.params[name];
        if (typeof value === "string") {
            path = path.replace(":" + name, encodeURIComponent(value));
        }
    }
    // A pattern with its own expression, such as the catch-all, is reported by
    // the name of its route.
    if (path.includes("(")) {
        path = "/" + String(to.name || "not-found");
    }
    return path || "/";
}

function socialSource(given: URLSearchParams): string {
    const value = (given.get(SOCIAL_PARAM) || "").trim().toLowerCase();
    return SOCIAL_SOURCES[value] || value.replace(/[^a-z0-9._-]/g, "").slice(0, 40);
}

export function campaignQuery(search: string = window.location.search): string {
    const given = new URLSearchParams(search);
    const kept = new URLSearchParams();
    for (const name of [...CAMPAIGN_PARAMS, ...CLICK_ID_PARAMS]) {
        const value = given.get(name);
        if (value) kept.set(name, value);
    }
    // A social label is the short form of the campaign labels. An address that
    // names its own source keeps it.
    const social = socialSource(given);
    if (social && !kept.has("utm_source")) {
        kept.set("utm_source", social);
        if (!kept.has("utm_medium")) kept.set("utm_medium", SOCIAL_MEDIUM);
        if (!kept.has("utm_campaign")) kept.set("utm_campaign", SOCIAL_CAMPAIGN);
    }
    const text = kept.toString();
    return text ? "?" + text : "";
}

function isProjectPage(to: RouteLocationNormalized): boolean {
    return to.matched.some((record) => record.path.startsWith(PROJECT_PATTERN));
}

function reportPage(to: RouteLocationNormalized) {
    const project = isProjectPage(to);
    const page = window.location.origin + routePattern(to);

    if (page !== lastPage) {
        const params: Record<string, string> = {
            // The first page keeps its campaign labels; GA reads them from here.
            page_location: lastPage ? page : page + campaignQuery(),
            page_title: project ? "Project: " + String(to.name || "") : document.title,
        };
        if (lastPage) params.page_referrer = lastPage;
        lastPage = page;
        // "set" first: every later event on this page then carries the same
        // address, not the one in the address bar with its ids and query string.
        gtag("set", params);
        gtag("event", "page_view", params);
    }

    const menu = project ? String(to.name || "") : "";
    if (menu && menu !== lastMenu) {
        gtag("event", "menu_" + menu);
    }
    lastMenu = menu;
}

function reportClick(e: MouseEvent) {
    const target = e.target as Element | null;
    const el = target?.closest?.("[data-track]") as HTMLElement | null;
    const name = el?.dataset.track;
    if (!el || !name) return;
    if (name.startsWith(LANDING_PREFIX) && router?.currentRoute.value.name !== LANDING_ROUTE) return;
    track(name, el.dataset.trackId ? { target: el.dataset.trackId } : undefined);
}

/**
 * Reports one event. `done` is for a caller that leaves the page right after
 * (the Google sign-in redirect): it runs once, as soon as the event has left or
 * after SEND_WAIT_MS, and at once when analytics is not running. A blocked
 * gtag.js therefore never holds a visitor on the page.
 */
export function track(name: string, params?: Record<string, string>, done?: () => void) {
    let finished = false;
    const finish = () => {
        if (finished) return;
        finished = true;
        if (done) done();
    };
    if (!started) {
        finish();
        return;
    }
    gtag("event", name, done ? { ...params, event_callback: finish } : { ...params });
    if (done) setTimeout(finish, SEND_WAIT_MS);
}

/** Loads gtag.js and starts reporting. Call once, as early as the router exists. */
export function startAnalytics(appRouter: Router) {
    if (started || !MEASUREMENT_ID || typeof window === "undefined") return;
    // A browser driven by a script (tests, screenshots, most crawlers) is not a visitor.
    if (navigator.webdriver) return;
    started = true;
    router = appRouter;

    window.dataLayer = window.dataLayer || [];
    window.gtag = gtag;
    gtag("consent", "default", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
    });
    gtag("js", new Date());
    gtag("config", MEASUREMENT_ID, {
        // Page views are reported by reportPage(), one per page and without ids.
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(MEASUREMENT_ID);
    document.head.appendChild(script);

    // The page the visitor landed on is reported now, not when the app has
    // mounted: mounting waits for the session, and a visit counts without it.
    reportPage(appRouter.resolve(window.location.pathname));
    appRouter.afterEach((to, _from, failure) => {
        if (!failure) reportPage(to);
    });
    document.addEventListener("click", reportClick, true);
}
